import React, { useState, useEffect, useRef } from "react";
import API from "../api";
import { BASE_URL } from "../config";

const GoogleAutocompleteInput = ({
    value,
    onChange,
    placeholder,
    types = "(regions)",
    filterCountry, // e.g. "CA", "US"
}) => {
    const isRegionType = String(types || "").includes("administrative_area_level_1");
    const isCountryType = String(types || "").includes("country");
    const [input, setInput] = useState(value || "");
    const [predictions, setPredictions] = useState([]);
    const [show, setShow] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    // Sync internal state with prop if it changes externally
    useEffect(() => {
        if (value !== input) setInput(value || "");
    }, [value]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                // Strict selection:
                // If the user typed something (input) that is different from the committed valid 'value',
                // AND they clicked outside without selecting a suggestion,
                // we must CLEAR the field (set to blank) rather than reverting to the old value.
                // User requirement: "should blank. should not go to previous one"

                if (input !== value) {
                    onChange(""); // Clear the parent state
                    setInput(""); // Clear local input
                }
                setShow(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [input, value, onChange]);

    // strict reset effect removed as it is handled in click handler now

    const fetchPredictions = async (text) => {
        if (!text) {
            setPredictions([]);
            setHasSearched(false);
            return;
        }
        setHasSearched(true);
        try {
            // Build query params
            // Legacy uses 'components=country:xx', New uses 'includedRegionCodes=xx'
            const params = { input: text, types };
            if (filterCountry) {
                params.components = `country:${filterCountry}`;
                params.includedRegionCodes = filterCountry;
            }

            const res = await API.get(`${BASE_URL}/api/places/autocomplete`, {
                params,
                credentials: true // if axios interceptor handles auth, or just standard
            });

            const list = res.data.predictions || [];
            // Google API returns everything; we rely on `types` filtering on server (legacy) 
            // or just list them.
            setPredictions(list);
        } catch (err) {
            console.error("Google Places predict error", err);
            setPredictions([]);
        }
    };

    const handleChange = (e) => {
        const txt = e.target.value;
        setInput(txt);
        setShow(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchPredictions(txt), 300);
    };


    const handleSelect = (pred) => {
        const mainText = pred?.structured_formatting?.main_text || "";
        const description = pred?.description || "";

        // Keep UI clean (region/country name only), but preserve full region
        // context in callback so parent logic can infer country from selection.
        const displayText = (isRegionType || isCountryType)
            ? (mainText || description.split(",")[0]?.trim() || "")
            : (description || mainText);
        const committedValue = isRegionType
            ? (description || displayText)
            : displayText;

        setInput(displayText);
        onChange(committedValue);
        setShow(false);
        setPredictions([]);
        setHasSearched(false);
    };

    const handleUseTypedValue = () => {
        if (isRegionType || isCountryType) return;
        const typed = String(input || "").trim();
        if (!typed) return;
        setInput(typed);
        onChange(typed);
        setShow(false);
        setPredictions([]);
        setHasSearched(false);
    };


    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={input}
                onChange={handleChange}
                onFocus={() => setShow(true)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={placeholder}
            />
            {show && (predictions.length > 0 || (hasSearched && String(input || "").trim())) && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {predictions.map((p) => (
                        <button
                            key={p.place_id}
                            type="button"
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700 transition-colors block border-b border-gray-100 last:border-0"
                            onClick={() => handleSelect(p)}
                        >
                            <span className="font-medium">
                                {isRegionType
                                    ? (p.description || p.structured_formatting?.main_text)
                                    : (p.structured_formatting?.main_text || p.description)}
                            </span>
                            {!isRegionType && (
                                <span className="text-xs text-gray-500 ml-2 block truncate">
                                    {p.description}
                                </span>
                            )}
                        </button>
                    ))}
                    {!(isRegionType || isCountryType) && predictions.length === 0 && hasSearched && String(input || "").trim() && (
                        <button
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-gray-700 transition-colors block border-b border-gray-100"
                            onClick={handleUseTypedValue}
                        >
                            Just Use "{String(input || "").trim()}"
                        </button>
                    )}
                    <div className="px-2 py-1 flex justify-end">
                        <img src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" alt="Powered by Google" className="h-4 opacity-75" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoogleAutocompleteInput;
